import { PMTiles, TileType, type Header } from 'pmtiles';
import type { Layer } from '@geosoft/viewer-sdk';

export interface PmtilesMetadata {
  readonly vector_layers?: unknown;
  readonly sourceLayer?: unknown;
  readonly source_layer?: unknown;
}

export interface PmtilesArchiveHeaderSummary {
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly bounds: readonly [number, number, number, number] | null;
  readonly center: readonly [number, number, number] | null;
  readonly tileType: string;
  readonly numAddressedTiles: number;
  readonly numTileEntries: number;
  readonly numTileContents: number;
}

export interface PmtilesArchiveInfo {
  readonly header: PmtilesArchiveHeaderSummary | null;
  readonly metadata: PmtilesMetadata | null;
  readonly vectorLayerIds: readonly string[];
}

export interface PmtilesSourceLayerResolution {
  readonly declared: string | null;
  readonly applied: string | null;
  readonly available: readonly string[];
}

export function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

export function parseVectorLayerIds(metadata: PmtilesMetadata | null | undefined): string[] {
  const raw = metadata?.vector_layers;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') {
          const id = (entry as { id?: unknown }).id;
          return typeof id === 'string' ? id : null;
        }
        return null;
      })
      .filter((id): id is string => Boolean(id && id.trim().length > 0));
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parseVectorLayerIds({ vector_layers: parsed });
    } catch {
      return raw.trim().length > 0 ? [raw.trim()] : [];
    }
  }
  return [];
}

export function readDeclaredPmtilesSourceLayer(layer: Layer): string | null {
  return firstNonEmptyString(
    layer.style?.['sourceLayer'],
    layer.style?.['source_layer'],
    (layer as unknown as { metadata?: PmtilesMetadata }).metadata?.sourceLayer,
    (layer as unknown as { metadata?: PmtilesMetadata }).metadata?.source_layer,
  );
}

export function isContourPmtilesLayer(layer: Pick<Layer, 'style'>): boolean {
  return (
    layer.style?.['terrainDerivative'] === 'contours'
    || layer.style?.['intakeKind'] === 'dem-contours'
  );
}

/**
 * Pure reconciliation of a declared PMTiles `source-layer` against the layer ids
 * actually present in the archive.
 *
 * Priority: exact archive match -> fuzzy archive match (case-insensitive /
 * substring, to absorb tippecanoe suffixes) -> archive's first layer -> the
 * declared name (archive metadata unavailable) -> kind heuristics -> `features`.
 */
export function reconcilePmtilesSourceLayer(
  declared: string | null,
  available: readonly string[],
  layer: Pick<Layer, 'id' | 'label'>,
  isContourDerivative: boolean,
): string | null {
  if (available.length > 0) {
    if (declared) {
      const exact = available.find((id) => id === declared);
      if (exact) return exact;
      const lower = declared.toLowerCase();
      const fuzzy = available.find(
        (id) =>
          id.toLowerCase() === lower ||
          id.toLowerCase().includes(lower) ||
          lower.includes(id.toLowerCase()),
      );
      return fuzzy ?? available[0];
    }
    return available[0];
  }

  if (declared) return declared;
  if (isContourDerivative) return 'contours';
  const id = layer.id.toLowerCase();
  const label = layer.label?.toLowerCase() ?? '';
  if (id.includes('street') || label.includes('street') || id.includes('road') || label.includes('road')) {
    return 'streets';
  }
  if (id.includes('building') || label.includes('building')) return 'buildings';
  if (id.includes('contour') || label.includes('contour')) return 'contours';
  return 'features';
}

export function resolvePmtilesSourceLayerFromInfo(
  layer: Layer,
  info: Pick<PmtilesArchiveInfo, 'vectorLayerIds'>,
): PmtilesSourceLayerResolution {
  const declared = readDeclaredPmtilesSourceLayer(layer);
  const available = info.vectorLayerIds;
  const applied = reconcilePmtilesSourceLayer(
    declared,
    available,
    layer,
    isContourPmtilesLayer(layer),
  );
  return { declared, applied, available };
}

export async function readPmtilesArchiveInfo(url: string): Promise<PmtilesArchiveInfo> {
  const archive = new PMTiles(url);
  const [header, metadata] = await Promise.all([
    archive.getHeader().catch(() => null),
    archive.getMetadata().catch(() => null),
  ]);
  const typedMetadata = asPmtilesMetadata(metadata);
  return {
    header: header ? summarizeHeader(header) : null,
    metadata: typedMetadata,
    vectorLayerIds: parseVectorLayerIds(typedMetadata),
  };
}

function asPmtilesMetadata(value: unknown): PmtilesMetadata | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as PmtilesMetadata)
    : null;
}

function summarizeHeader(header: Header): PmtilesArchiveHeaderSummary {
  const bounds: readonly [number, number, number, number] | null =
    [header.minLon, header.minLat, header.maxLon, header.maxLat].every(Number.isFinite)
      ? [header.minLon, header.minLat, header.maxLon, header.maxLat]
      : null;
  const center: readonly [number, number, number] | null =
    [header.centerLon, header.centerLat, header.centerZoom].every(Number.isFinite)
      ? [header.centerLon, header.centerLat, header.centerZoom]
      : null;
  return {
    minZoom: header.minZoom,
    maxZoom: header.maxZoom,
    bounds,
    center,
    tileType: tileTypeLabel(header.tileType),
    numAddressedTiles: header.numAddressedTiles,
    numTileEntries: header.numTileEntries,
    numTileContents: header.numTileContents,
  };
}

function tileTypeLabel(tileType: TileType): string {
  switch (tileType) {
    case TileType.Mvt:
      return 'mvt';
    case TileType.Png:
      return 'png';
    case TileType.Jpeg:
      return 'jpeg';
    case TileType.Webp:
      return 'webp';
    case TileType.Avif:
      return 'avif';
    case TileType.Mlt:
      return 'mlt';
    case TileType.Unknown:
    default:
      return 'unknown';
  }
}
